import React, {useMemo, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {PageContainer, PageHeader} from "../../../shared/components";
import {useFaqs} from "../queries";
import type {FaqItem} from "../api";

type CategoryGroup = { category: string; items: FaqItem[] };

export const Faq: React.FC = () => {
    const {data, isLoading, isError, error, refetch} = useFaqs({
        limit: 50,
        offset: 0,
        is_published: true,
    });
    const [query, setQuery] = useState("");

    const groups = useMemo<CategoryGroup[]>(() => {
        const items = [...(data ?? [])]
            .filter((item) => item.isPublished)
            .sort((a, b) => a.sortOrder - b.sortOrder);

        const normalizedQuery = query.trim().toLowerCase();
        const filtered =
            normalizedQuery.length === 0
                ? items
                : items.filter((item) => {
                    const haystack = [
                        item.header,
                        item.description,
                        item.category,
                        ...(item.tags ?? []),
                    ]
                        .join(" ")
                        .toLowerCase();
                    return haystack.includes(normalizedQuery);
                });

        const byCategory: Record<string, FaqItem[]> = {};
        for (const item of filtered) {
            byCategory[item.category] = byCategory[item.category] ?? [];
            byCategory[item.category].push(item);
        }

        return Object.entries(byCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, items]) => ({category, items}));
    }, [data, query]);

    return (
        <Box sx={{width: "100%", height: "100%", overflow: "auto"}}>
            <PageContainer maxWidth="lg" sx={{my: 4}}>
                <PageHeader
                    title="FAQ"
                    subtitle="Answers to common questions about the programme"
                    align="left"
                />

                <Stack spacing={2}>
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search FAQs"
                        size="medium"
                        fullWidth
                    />

                    {isLoading && (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress/>
                        </Box>
                    )}

                    {isError && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => refetch()}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            Failed to load FAQs
                            {error instanceof Error ? `: ${error.message}` : null}
                        </Alert>
                    )}

                    {!isLoading && !isError && groups.length === 0 && (
                        <Alert severity="info">No FAQs found.</Alert>
                    )}

                    {!isLoading &&
                        !isError &&
                        groups.map((group) => (
                            <Box key={group.category} sx={{pt: 1}}>
                                <Typography variant="h6" sx={{mb: 1.5}}>
                                    {group.category}
                                </Typography>
                                <Stack spacing={1}>
                                    {group.items.map((item) => (
                                        <Accordion
                                            key={item.id}
                                            sx={{
                                                borderRadius: 2,
                                                "&:before": {display: "none"},
                                                "& .MuiAccordionSummary-root": {
                                                    px: {xs: 2, md: 3},
                                                    py: {xs: 1.5, md: 2},
                                                    minHeight: "unset",
                                                },
                                                "& .MuiAccordionSummary-content": {my: 0},
                                                "& .MuiAccordionDetails-root": {
                                                    px: {xs: 2, md: 3},
                                                    pb: {xs: 2, md: 3},
                                                },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreRoundedIcon/>}
                                            >
                                                <Typography variant="h6" fontWeight={600}>
                                                    {item.header}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={1.5}>
                                                    <Typography variant="h6" color="text.secondary">
                                                        {item.description}
                                                    </Typography>
                                                    {item.tags?.length ? (
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            useFlexGap
                                                            flexWrap="wrap"
                                                        >
                                                            {item.tags.map((tag) => (
                                                                <Chip
                                                                    key={tag}
                                                                    label={tag}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : null}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                </Stack>
            </PageContainer>
        </Box>
    );
};
